import { supportsObjectFit } from 'utils/general';
import obzerv from 'lib/intersection';

/**
 * A Vue.js component to render images within
 * a Vue.js application.
 *
 * @param {String} src The default image src
 * @param {String} srcset An option srcset value
 * @param {String} alt Alt text to represent the image
 * @param {String} css CSS classes to add to the parent
 * @param {String} fit "cover" or "contain"
 * @param {String} pos "center", "top" or "bottom"
 * @param {String} width Image width
 * @param {String} height Image height
 * @param {Boolean} lazy Should the image load in lazily
 * @param {Boolean} transition Should there be a fade in transition?
 *
 * @example
 * <div
 *   is="vue-image"
 *   :src="imageSrc"
 *   :alt="title"
 *   fit="cover"
 *   css="product-card__image"
 *   width="100px"
 *   height="200px" />
 */
export default {
  name: 'vue-image',
  props: {
    src: String,
    srcset: String,
    alt: String,
    css: String,
    fit: String,
    pos: String,
    width: String,
    height: String,
    srcMobile: {
      type: String,
      default: '',
    },
    srcTablet: {
      type: String,
      default: '',
    },
    useSource: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      default: 0,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
    transition: {
      type: Boolean,
      default: true,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    slidevalue: {
      type: String,
    },
  },
  data() {
    return {
      mainImageSrc: '',
      secondaryImgSrc: '',
      objectFitOk: supportsObjectFit(),
      ownSrcset: this.srcset,
      regex: {
        SIZE: /_\d*x([.])/,
        MIME: /([.](?:jpe?g|png|webp|gif))/,
      },
    };
  },
  mounted() {
    this.prepareNewImage();
    this.onResize();
  },
  created() {
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    src() {
      this.onResize();
    },
    srcTablet() {
      this.onResize();
    },
    srcMobile() {
      this.onResize();
    },
  },
  computed: {
    imgFit() {
      return this.fullWidth
        ? 'full-width'
        : this.fit
        ? `fit-${this.fit}`
        : 'fit-cover';
    },
    imgPos() {
      return this.pos ? `pos-${this.pos}` : 'pos-center';
    },
    backgroundImage() {
      const sources = (this.ownSrcset || this.src)
        .split(/\s?,\s?/)
        .reduce((o, val) => {
          const brk = val.split(/\s|w$/);
          o[brk[1] || 0] = brk[0];
          return o;
        }, {});
      return sources[Math.max(...Object.keys(sources))];
    },
    isOutsideSource() {
      if (!this.src) return false;
      const domains = [
        'yotpo.com',
        'kaltura.com', // Yotpo's alternate domain
        'youtube.com',
        'accentuate.io',
        'cdninstagram.com', // UGC images
      ];
      const matches = domains.filter((domain) => ~this.src.indexOf(domain));
      return matches.length > 0;
    },
    computedSrc() {
      if (!this.src) return '';
      // @max Will do accentuate mutations later
      if (this.isOutsideSource) {
        return this.src;
      }
      return this.replaceSize(
        this.src,
        this.size > 0 ? this.size : this.getImageSize(),
      );
    },
    computedStyles() {
      if (this.slidevalue) {
        return {
          clipPath: `polygon(0 0, ${Number(this.slidevalue)}% 0, ${Number(
            this.slidevalue,
          )}% 100%, 0 100%)`,
        };
      }
      if (this.objectFitOk) {
        return {};
      }
      return { backgroundImage: 'url(' + this.backgroundImage + ')' };
    },
  },
  methods: {
    getImageSize() {
      return Math.ceil(Math.round(this.width * 1.5) / 100) * 100;
    },
    prepareNewImage() {
      const img = new Image();
      if (this.src) {
        img.src = this.src;
      }
    },
    replaceSize(src, size) {
      return size > 0
        ? src
            .replace(this.regex.SIZE, '$1')
            .replace(this.regex.MIME, `_${size}x$1`)
        : src;
    },
    onResize() {
      if (window.innerWidth > 1024) {
        this.ownSrcset = this.src;
      } else if (window.innerWidth > 500) {
        this.ownSrcset = this.srcTablet;
      } else {
        this.ownSrcset = this.srcMobile;
      }
    },
  },
  template: `
    <component :is="useSource ? 'picture' : 'div'" class="img" :class="[css, imgFit, imgPos, {'no-transition': !transition}]" ref="wrapper">
      <source v-if="useSource" :srcset="src" media="(min-width: 1024px)">
      <source v-if="useSource && srcTablet" :srcset="srcTablet" media="(min-width: 415px)">
      <source v-if="useSource && srcMobile" :srcset="srcMobile">
      <transition name="fade" mode="in-out" v-if="transition">
        <img
          :is="objectFitOk ? 'img' : 'div'"
          :src="computedSrc"
          :srcset="ownSrcset"
          :alt="alt"
          :title="alt"
          :data-fit="fit"
          :key="src"
          :class="{'img__el': !fullWidth, 'img__el--ie': !objectFitOk}"
          :style="computedStyles"
          :loading="objectFitOk && lazy ? 'lazy' : 'false'"
          :width="width"
          :height="height"
          />
      </transition>
        <img
        v-else
        :is="objectFitOk ? 'img' : 'div'"
        :src="computedSrc"
        :srcset="ownSrcset"
        :alt="alt"
        :title="alt"
        :data-fit="fit"
        :key="src"
        :class="{'img__el': !fullWidth, 'img__el--ie': !objectFitOk}"
        :style="computedStyles"
        :loading="objectFitOk && lazy ? 'lazy' : 'false'"
        :width="width"
        :height="height"
         />
    </component>
  `,
};
